import { useState, useEffect, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, Pencil } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
  RowActions,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Account = { id: number; name: string }
type Contact = { id: number; full_name: string; account: number | null }
type Stage = {
  id: number; pipeline: number; name: string; probability: string;
  sort_order: number; is_won: boolean; is_lost: boolean; color: string
}
type Pipeline = {
  id: number; name: string; is_default: boolean; is_active: boolean; stages: Stage[]
}

type Opportunity = {
  id: number
  name: string
  account: number
  account_name: string
  primary_contact: number | null
  primary_contact_name: string | null
  amount: string
  currency_code: string
  pipeline: number
  pipeline_name: string
  stage: number
  stage_name: string
  stage_color: string
  probability: string
  probability_override: string | null
  weighted_amount: string
  expected_close_date: string | null
  owner: number | null
  owner_username: string | null
  status: 'OPEN' | 'WON' | 'LOST'
  closed_at: string | null
  lost_reason: string
  notes: string
  converted_to_quote: number | null
  converted_quote_number: string | null
}

type Activity = {
  id: number
  activity_type: 'CALL' | 'EMAIL' | 'MEETING' | 'NOTE' | 'TASK'
  subject: string
  description: string
  related_opportunity: number | null
  related_opportunity_name: string | null
  owner: number | null
  owner_username: string | null
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED'
  due_date: string | null
  completed_at: string | null
  duration_minutes: number | null
  is_overdue: boolean
}

// ─────────────────── Constants ───────────────────
const STATUS_COLOR: Record<Opportunity['status'], string> = {
  OPEN: 'bg-blue-100 text-blue-800',
  WON: 'bg-green-100 text-green-800',
  LOST: 'bg-red-100 text-red-700',
}

const ACTIVITY_STATUS_COLOR: Record<Activity['status'], string> = {
  PENDING: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
}

const ACTIVITY_TYPE_LABEL: Record<Activity['activity_type'], string> = {
  CALL: 'Call', EMAIL: 'Email', MEETING: 'Meeting', NOTE: 'Note', TASK: 'Task',
}

type TabKey = 'details' | 'activities' | 'files'

// ─────────────────── Page ───────────────────
export default function CRMOpportunityDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const oppId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const oppQ = useQuery({
    queryKey: ['crm', 'opportunity', oppId],
    queryFn: async () => (await api.get<Opportunity>(`/crm/opportunities/${oppId}/`)).data,
    enabled: !!oppId,
  })
  const accountsQ = useQuery({
    queryKey: ['crm', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/crm/accounts/')).data,
  })
  const contactsQ = useQuery({
    queryKey: ['crm', 'contacts'],
    queryFn: async () => (await api.get<Contact[]>('/crm/contacts/')).data,
  })
  const pipelinesQ = useQuery({
    queryKey: ['crm', 'pipelines'],
    queryFn: async () => (await api.get<Pipeline[]>('/crm/pipelines/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Opportunity>) =>
      (await api.patch(`/crm/opportunities/${oppId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'opportunity', oppId] })
      qc.invalidateQueries({ queryKey: ['crm', 'opportunities'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/crm/opportunities/${oppId}/`),
    onSuccess: () => {
      toast.success('Opportunity deleted')
      qc.invalidateQueries({ queryKey: ['crm', 'opportunities'] })
      navigate('/admin/crm/opportunities')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const convertQuoteMut = useMutation({
    mutationFn: async () => (await api.post(`/crm/opportunities/${oppId}/convert_to_quote/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm'] })
      qc.invalidateQueries({ queryKey: ['sales', 'quotes'] })
      toast.success('Converted to quote')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const convertEngagementMut = useMutation({
    mutationFn: async () => (await api.post(`/crm/opportunities/${oppId}/convert_to_engagement/`)).data,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['crm'] })
      qc.invalidateQueries({ queryKey: ['consulting', 'engagements'] })
      toast.success('Converted to engagement')
      // Drop the user on the new engagement record page.
      // The endpoint returns engagement_id, not id.
      if (data?.engagement_id) navigate(`/admin/consulting/engagements/${data.engagement_id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (oppQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (oppQ.isError || !oppQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Opportunity not found.</p>
          <Link to="/admin/crm/opportunities" className="text-blue-600 hover:underline">← Back to opportunities</Link>
        </div>
      </AdminLayout>
    )
  }

  const opp = oppQ.data
  const accounts = accountsQ.data || []
  const contacts = contactsQ.data || []
  const pipelines = (pipelinesQ.data || []).filter((p) => p.is_active)

  const save = (payload: Partial<Opportunity>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  const currentPipeline = pipelines.find((p) => p.id === opp.pipeline)
  const currentStages = currentPipeline?.stages || []
  const eligibleContacts = contacts.filter((c) => c.account === opp.account)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/crm/opportunities" className="text-muted-foreground hover:text-foreground hover:underline">← Opportunities</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText value={opp.name} onSave={(v) => save({ name: v })} />
            </h2>
            <div className="text-sm text-muted-foreground">
              <Link to={`/admin/crm/accounts/${opp.account}`} className="hover:underline">
                {opp.account_name}
              </Link>
              <span> · </span>
              {opp.pipeline_name}
              <span> · </span>
              <span
                className="px-2 py-0.5 rounded text-xs font-semibold"
                style={{ backgroundColor: (opp.stage_color || '#94a3b8') + '33', color: opp.stage_color || '#475569' }}
              >
                {opp.stage_name}
              </span>
              {opp.converted_quote_number && (
                <span className="text-xs text-muted-foreground"> · → {opp.converted_quote_number}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={opp.status}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'OPEN', label: 'Open' },
                { value: 'WON', label: 'Won' },
                { value: 'LOST', label: 'Lost' },
              ]}
              onSave={(v) => save({ status: v })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!opp.converted_to_quote && (
                  <DropdownMenuItem
                    onClick={() => convertQuoteMut.mutate()}
                    disabled={convertQuoteMut.isPending}
                    className="text-green-700"
                  >
                    Convert to quote
                  </DropdownMenuItem>
                )}
                {opp.status === 'WON' && (
                  <DropdownMenuItem
                    onClick={() => convertEngagementMut.mutate()}
                    disabled={convertEngagementMut.isPending}
                    className="text-purple-700"
                  >
                    Convert to engagement
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  disabled={!!opp.converted_to_quote}
                  onClick={() => !opp.converted_to_quote && confirm(
                    `opportunity ${opp.name}`,
                    () => deleteMut.mutate(),
                  )}
                  className={!opp.converted_to_quote ? 'text-destructive' : ''}
                >
                  {opp.converted_to_quote
                    ? `Delete (converted to ${opp.converted_quote_number})`
                    : 'Delete'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'activities', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'activities' && 'Activities'}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && (
        <DetailsTab
          opp={opp}
          accounts={accounts}
          eligibleContacts={eligibleContacts}
          pipelines={pipelines}
          currentStages={currentStages}
          onSave={save}
        />
      )}
      {tab === 'activities' && (
        <ActivitiesTab opportunityId={opp.id} opportunityName={opp.name} />
      )}
      {tab === 'files' && <FilesTab parent="crm.opportunity" parentId={oppId} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  opp, accounts, eligibleContacts, pipelines, currentStages, onSave,
}: {
  opp: Opportunity
  accounts: Account[]
  eligibleContacts: Contact[]
  pipelines: Pipeline[]
  currentStages: Stage[]
  onSave: (payload: Partial<Opportunity>) => Promise<void>
}) {
  // Pipeline + stage editing is a multi-field interaction (changing pipeline
  // resets stage), so it's a drawer rather than an inline edit.
  const [psOpen, setPsOpen] = useState(false)
  const [psDraft, setPsDraft] = useState({ pipeline: opp.pipeline, stage: opp.stage })
  const [psPending, setPsPending] = useState(false)

  useEffect(() => {
    if (psOpen) setPsDraft({ pipeline: opp.pipeline, stage: opp.stage })
  }, [psOpen, opp.pipeline, opp.stage])

  const psPipeline = pipelines.find((p) => p.id === psDraft.pipeline)
  const psStages = psPipeline?.stages || []

  async function savePipelineStage() {
    setPsPending(true)
    try {
      await onSave({ pipeline: psDraft.pipeline, stage: psDraft.stage })
      toast.success('Pipeline & stage updated'); setPsOpen(false)
    } catch (e) { toast.error(extractErr(e)) }
    finally { setPsPending(false) }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <Section title="Identity">
        <Row label="Opportunity name">
          <InlineText value={opp.name} onSave={(v) => onSave({ name: v })} />
        </Row>
        <Row label="Account">
          <InlineSelect
            value={String(opp.account)}
            displayLabel={opp.account_name}
            options={accounts.map((a) => ({
              value: String(a.id), label: a.name, searchText: a.name,
            }))}
            onSave={(v) => onSave({ account: Number(v), primary_contact: null })}
            helper="Changing the account clears the primary contact (contacts belong to accounts)."
          />
        </Row>
        <Row label="Primary contact">
          <InlineSelect
            value={opp.primary_contact ? String(opp.primary_contact) : ''}
            displayLabel={opp.primary_contact_name || <Dash />}
            allowClear
            options={eligibleContacts.map((c) => ({
              value: String(c.id), label: c.full_name, searchText: c.full_name,
            }))}
            onSave={(v) => onSave({ primary_contact: v ? Number(v) : null })}
            helper={eligibleContacts.length === 0
              ? 'No contacts at this account. Add contacts on the account record page.'
              : undefined}
          />
        </Row>
        <Row label="Owner">
          <span className="text-sm">{opp.owner_username || <Dash />}</span>
        </Row>
      </Section>

      <Section title="Pipeline & stage">
        <div className="col-span-2 flex items-start justify-between">
          <dl className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm flex-1">
            <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-0.5">Pipeline</dt>
            <dd>{opp.pipeline_name}</dd>
            <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-0.5">Current stage</dt>
            <dd>
              <span className="px-2 py-0.5 rounded text-xs font-semibold"
                style={{ backgroundColor: (opp.stage_color || '#94a3b8') + '33', color: opp.stage_color || '#475569' }}>
                {opp.stage_name}
              </span>
            </dd>
          </dl>
          <Button size="sm" variant="outline" onClick={() => setPsOpen(true)}>
            <Pencil className="h-3 w-3 mr-1" /> Change
          </Button>
        </div>

        {/* Quick stage change within current pipeline (single-field, so inline). */}
        {currentStages.length > 0 && (
          <Row label="Quick move">
            <InlineSelect
              value={String(opp.stage)}
              displayLabel={
                <span className="px-2 py-0.5 rounded text-xs font-semibold"
                  style={{ backgroundColor: (opp.stage_color || '#94a3b8') + '33', color: opp.stage_color || '#475569' }}>
                  {opp.stage_name}
                </span>
              }
              options={currentStages.map((s) => ({
                value: String(s.id), label: s.name, searchText: s.name,
              }))}
              onSave={(v) => onSave({ stage: Number(v) })}
              helper={`Choose a stage within ${opp.pipeline_name}.`}
            />
          </Row>
        )}
      </Section>

      <Section title="Forecast">
        <Row label="Amount">
          <InlineNumber
            value={opp.amount}
            onSave={(v) => onSave({ amount: v })}
            displayFormat={(v) => v
              ? <span className="font-mono">{Number(v).toFixed(2)} {opp.currency_code}</span>
              : <Dash />}
          />
        </Row>
        <Row label="Currency">
          <InlineText
            value={opp.currency_code}
            onSave={(v) => onSave({ currency_code: v.toUpperCase().slice(0, 3) })}
            className="font-mono"
          />
        </Row>
        <Row label="Probability">
          <div className="text-sm">
            <span className="font-mono">{Number(opp.probability).toFixed(0)}%</span>
            {opp.probability_override !== null && (
              <span className="text-xs text-muted-foreground ml-2">(override; stage default: {Number(opp.probability).toFixed(0)}%)</span>
            )}
          </div>
        </Row>
        <Row label="Probability override">
          <InlineNumber
            value={opp.probability_override || ''}
            step="1"
            placeholder="leave empty to use stage default"
            onSave={(v) => onSave({ probability_override: v === '' ? null : v } as Partial<Opportunity>)}
            displayFormat={(v) => v
              ? <span className="font-mono">{Number(v).toFixed(0)}%</span>
              : <span className="text-muted-foreground italic">use stage default</span>}
            helper="0-100. Leave empty to inherit the stage's default probability."
          />
        </Row>
        <Row label="Weighted amount">
          <span className="text-sm font-mono text-muted-foreground">
            {Number(opp.weighted_amount).toFixed(2)} {opp.currency_code}
            <span className="ml-2 text-xs italic">(computed = amount × probability)</span>
          </span>
        </Row>
        <Row label="Expected close date">
          <InlineDate
            value={opp.expected_close_date}
            onSave={(v) => onSave({ expected_close_date: v })}
          />
        </Row>
      </Section>

      {opp.status === 'LOST' && (
        <Section title="Lost reason">
          <Row label="Reason">
            <InlineText
              value={opp.lost_reason}
              multiline
              onSave={(v) => onSave({ lost_reason: v })}
              placeholder="Why was this lost? Helps future forecasting."
            />
          </Row>
        </Section>
      )}

      <Section title="Notes">
        <Row label="Notes">
          <InlineText
            value={opp.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            placeholder="Internal notes about this opportunity"
          />
        </Row>
      </Section>

      <RecordDrawer
        open={psOpen}
        onOpenChange={setPsOpen}
        title="Change pipeline & stage"
        description="Switching pipelines resets the stage. The probability will be recalculated from the new stage's default unless you've set an override."
        onSubmit={savePipelineStage}
        isPending={psPending}
        submitLabel="Save"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="ps-pipeline">Pipeline</FieldLabel>
            <SearchableSelect
              id="ps-pipeline"
              value={String(psDraft.pipeline)}
              onChange={(v) => {
                const pid = Number(v)
                const firstStage = pipelines.find((p) => p.id === pid)?.stages[0]?.id ?? 0
                setPsDraft({ pipeline: pid, stage: firstStage })
              }}
              options={pipelines.map((p) => ({ value: String(p.id), label: p.name }))}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="ps-stage">Stage</FieldLabel>
            <SearchableSelect
              id="ps-stage"
              value={String(psDraft.stage)}
              onChange={(v) => setPsDraft({ ...psDraft, stage: Number(v) })}
              options={psStages.map((s) => ({
                value: String(s.id),
                label: `${s.name} (${Number(s.probability).toFixed(0)}%)`,
              }))}
            />
            <FieldDescription>Stage probability shown in parentheses.</FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Activities tab ───────────────────

type ActivityDraft = {
  activity_type: Activity['activity_type']
  subject: string
  description: string
  due_date: string | null
  duration_minutes: number | null
  status: Activity['status']
}

function emptyActivityDraft(): ActivityDraft {
  return {
    activity_type: 'TASK', subject: '', description: '',
    due_date: null, duration_minutes: null, status: 'PENDING',
  }
}

function ActivitiesTab({ opportunityId, opportunityName }:
  { opportunityId: number; opportunityName: string }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const activitiesQ = useQuery({
    queryKey: ['crm', 'activities', { opportunity: opportunityId }],
    queryFn: async () => (await api.get<Activity[]>(`/crm/activities/?opportunity=${opportunityId}`)).data,
  })

  const [editing, setEditing] = useState<Activity | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<ActivityDraft>(emptyActivityDraft())

  function openCreate() {
    setEditing(null)
    setDraft(emptyActivityDraft())
    setCreating(true)
  }
  function openEdit(a: Activity) {
    setCreating(false); setEditing(a)
    setDraft({
      activity_type: a.activity_type, subject: a.subject, description: a.description,
      due_date: a.due_date, duration_minutes: a.duration_minutes, status: a.status,
    })
  }
  function closeDrawer() { setCreating(false); setEditing(null) }

  const createMut = useMutation({
    mutationFn: async () => (await api.post('/crm/activities/', {
      ...draft,
      related_opportunity: opportunityId,
      related_lead: null, related_contact: null, related_account: null,
      owner: null,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'activities'] })
      toast.success('Activity added'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const editMut = useMutation({
    mutationFn: async () => (await api.patch(`/crm/activities/${editing!.id}/`, draft)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'activities'] })
      toast.success('Activity saved'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const inlineMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Activity> }) =>
      (await api.patch(`/crm/activities/${id}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'activities'] }),
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/crm/activities/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'activities'] })
      toast.success('Activity deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const saveField = async (id: number, payload: Partial<Activity>) => {
    await inlineMut.mutateAsync({ id, payload })
  }

  const activities = activitiesQ.data || []

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Calls, emails, meetings, and tasks logged against this opportunity.
        </p>
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add activity</Button>
      </div>

      {activitiesQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : activities.length === 0 ? (
        <p className="text-muted-foreground text-sm">No activities logged yet.</p>
      ) : (
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40">
            <tr>
              <Th>Type</Th><Th>Subject</Th><Th>Due</Th><Th>Owner</Th><Th>Status</Th><Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {activities.map((a) => (
              <tr key={a.id} className={a.is_overdue && a.status === 'PENDING' ? 'bg-amber-50/50' : ''}>
                <Td className="text-xs font-semibold uppercase">{ACTIVITY_TYPE_LABEL[a.activity_type]}</Td>
                <Td className="font-medium">
                  <InlineText value={a.subject} onSave={(v) => saveField(a.id, { subject: v })} />
                </Td>
                <Td className="text-xs">
                  <InlineDate value={a.due_date} onSave={(v) => saveField(a.id, { due_date: v })} />
                  {a.is_overdue && a.status === 'PENDING' && (
                    <div className="text-xs text-amber-700 mt-0.5">overdue</div>
                  )}
                </Td>
                <Td className="text-xs">{a.owner_username || <Dash />}</Td>
                <Td>
                  <InlineStatus
                    value={a.status}
                    colorMap={ACTIVITY_STATUS_COLOR}
                    options={[
                      { value: 'PENDING', label: 'Pending' },
                      { value: 'COMPLETED', label: 'Completed' },
                      { value: 'CANCELLED', label: 'Cancelled' },
                    ]}
                    onSave={(v) => saveField(a.id, { status: v })}
                  />
                </Td>
                <Td className="text-right">
                  <RowActions
                    onEdit={() => openEdit(a)}
                    onDelete={() => confirm(
                      `${ACTIVITY_TYPE_LABEL[a.activity_type]} "${a.subject}"`,
                      () => deleteMut.mutate(a.id),
                    )}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <RecordDrawer
        open={creating || editing !== null}
        onOpenChange={(v) => { if (!v) closeDrawer() }}
        title={editing ? 'Edit activity' : 'New activity'}
        description={editing
          ? 'Update this activity.'
          : `Log a call, email, meeting, note, or task against ${opportunityName}.`}
        onSubmit={() => editing ? editMut.mutate() : createMut.mutate()}
        isPending={createMut.isPending || editMut.isPending}
        submitLabel={editing ? 'Save' : 'Add activity'}
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="ac-type">Type</FieldLabel>
            <Select value={draft.activity_type}
              onValueChange={(v) => setDraft({ ...draft, activity_type: v as Activity['activity_type'] })}>
              <SelectTrigger id="ac-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(ACTIVITY_TYPE_LABEL) as Activity['activity_type'][]).map((k) => (
                  <SelectItem key={k} value={k}>{ACTIVITY_TYPE_LABEL[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel htmlFor="ac-subject">Subject *</FieldLabel>
            <Input id="ac-subject" autoFocus required value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="ac-desc">Description</FieldLabel>
            <Textarea id="ac-desc" rows={3} value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="ac-due">Due date</FieldLabel>
              <DatePicker id="ac-due" value={draft.due_date || ''}
                onChange={(v) => setDraft({ ...draft, due_date: v || null })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="ac-duration">Duration (min)</FieldLabel>
              <Input id="ac-duration" type="number" min="0"
                value={draft.duration_minutes ?? ''}
                onChange={(e) => setDraft({
                  ...draft,
                  duration_minutes: e.target.value === '' ? null : Number(e.target.value),
                })}
                className="font-mono text-right" />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="ac-status">Status</FieldLabel>
            <Select value={draft.status}
              onValueChange={(v) => setDraft({ ...draft, status: v as Activity['status'] })}>
              <SelectTrigger id="ac-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
